<template>
  <div class="w-full min-w-[288px]">
    <div class="flex justify-between items-baseline p-3">
      <div class="inline-flex items-center">
        <SymbolTradeStateIcon
          class="me-2"
          :trading-state-code="order.instrumentTradingState"
        />
        <div class="flex flex-col">
          <Retail v-if="getSymbolIsin(order)" :isin="getSymbolIsin(order)">
            <NText weight="bold">
              {{ order.instrumentName }}
            </NText>
          </Retail>
          <NText
            variant="overline"
            weight="medium"
            class="text-neutral-secondary"
          >
            {{ order.instrumentTitle }}
          </NText>
        </div>
      </div>

      <div class="flex flex-col items-baseline">
        <NumberFormatter #="{ number }" :value="order.lastTradePrice">
          <NText :key="number" variant="h3" tag="h3" weight="bold">
            {{ number }}
          </NText>
        </NumberFormatter>

        <NumberFormatter
          #="{ number }"
          :value="(order.referencePriceVariationPercentage ?? 0) / 100"
          maximum-fraction-digits="2"
          sign-display="always"
          number-style="percent"
        >
          <NText
            weight="bold"
            variant="caption1"
            dir="ltr"
            :class="{
              'text-brand-primary':
                (order.referencePriceVariationPercentage ?? 0) > 0,
              'text-critical-primary':
                (order.referencePriceVariationPercentage ?? 0) < 0,
            }"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>
    </div>

    <div class="bg-neutral-secondary px-4 mt-2 rounded-md">
      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('orders.orderSide') }}
        </NText>

        <NText
          v-if="order.orderSideId === ORDER_SIDES.buy"
          variant="caption1"
          weight="medium"
          class="pr-2 text-brand-primary"
        >
          {{ t('common.buy') }}
        </NText>

        <NText
          v-if="order.orderSideId === ORDER_SIDES.sell"
          variant="caption1"
          weight="medium"
          class="pr-2 text-critical-primary"
        >
          {{ t('common.sell') }}
        </NText>
      </div>

      <div
        class="flex justify-between items-center py-2 border-b border-neutral-tertiary"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.order_state') }}
        </NText>

        <NText variant="caption1" weight="medium" class="pr-2">
          {{ order.orderState }}
        </NText>
      </div>

      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('misc.quantity') }} ({{ t('modules.order.executed') }})
        </NText>

        <div>
          <NumberFormatter #="{ number }" :value="order.executedQuantity">
            <NText variant="caption1" weight="medium"> ({{ number }}) </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="order.quantity">
            <NText variant="caption1" weight="medium">{{ number }}</NText>
          </NumberFormatter>
        </div>
      </div>

      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.order_price') }}
        </NText>

        <NumberFormatter #="{ number }" :value="order.price">
          <NText variant="caption1" weight="medium" class="pr-2">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>

      <div
        class="flex justify-between items-center py-2 pb-2 border-b border-neutral-tertiary"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.order_value') }}
        </NText>

        <NumberFormatter #="{ number }" :value="totalValue" round>
          <NText variant="caption1" weight="medium" class="pr-2">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>

      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.creationTime') }}
        </NText>

        <NText variant="caption1" weight="medium" class="pr-2">
          {{ creationTime }}
        </NText>
      </div>

      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('orders.valid_until') }}
        </NText>

        <NText
          v-if="order.validityTypeId === VALIDITY_TYPES.GoodTillDate"
          variant="caption1"
          weight="medium"
        >
          {{ d(order.validityDate, 'shortDate') }}
        </NText>

        <NText v-else variant="caption1" weight="medium">
          {{
            order.validityTypeId === VALIDITY_TYPES.Day
              ? validityDayTitle
              : t('common.cancellation')
          }}
        </NText>
      </div>

      <div v-if="order.errorDescription" class="flex justify-between py-3">
        <NText
          variant="caption1"
          weight="medium"
          class="text-neutral-secondary-hover"
        >
          {{ t('modules.order.description') }}
        </NText>
        <NText variant="caption1" weight="bold" class="mr-4">
          {{ order.errorDescription }}
        </NText>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  calcSymbolBuyValue,
  calcSymbolSellValue,
  getSymbolIsin,
  useSymbolDeductions,
} from '@core/symbol';
import { useI18n } from 'vue-i18n';
import { OrderHistoryItem } from '@libs/api-reference';
import { computed } from 'vue';
import { ORDER_SIDES, VALIDITY_TYPES } from '@core/order';

import { Retail, SymbolTradeStateIcon } from '@/modules/symbol';

import NumberFormatter from '@/components/Number.vue';

type OrderHistoryDialogProps = {
  order: OrderHistoryItem;
};

const props = defineProps<OrderHistoryDialogProps>();

const { findWageConfigBySymbol } = useSymbolDeductions();

const { t, d } = useI18n();

const creationTime = computed(
  () => `${d(props.order.entryDate, 'shortDate')} ,
    ${d(props.order.entryDate, 'shortTime')}`
);

const totalValue = computed(() => {
  const wage = findWageConfigBySymbol(props.order);

  return props.order.orderSideId === ORDER_SIDES.buy
    ? calcSymbolBuyValue(props.order, wage)
    : calcSymbolSellValue(props.order, wage);
});

const validityDayTitle = computed(() => {
  const creationDay = new Date(props.order.entryDate).getDate();
  const today = new Date().getDate();

  return creationDay === today
    ? t('misc.today')
    : d(props.order.entryDate, 'shortDate');
});
</script>
